/**
 * Filing Info cell updates: PA-required status changes and modal saves.
 * @module updateRowData/filingInfoReducer
 */
import { FilingInfoSavePayload } from "../../../../../../components/modals/FilingInfoModal/filingInfoModal.utils";
import { CptCodesTableRowModel } from "../../../../../../data-model/cptCodesTableModel";
import { FILING_MODE } from "../../../../../../enums/filingMode";
import { MedicalPaOrdersAuthStatus } from "../../../../../../enums/medicalPaOrdersAuthStatus";
import { CptCodeWithDetailsKeys } from "../../../../../../enums/tableColumnKeys";
import {
  getFilingInfoRequired,
  shouldTheFilingInfoBeStatic,
} from "../../../../../../utils/getFilingInfoRequired";

/**
 * Recomputes the Filing Info cell after a PA-required status change.
 *
 * `isRequired` comes from {@link getFilingInfoRequired}; `isStatic` from
 * {@link shouldTheFilingInfoBeStatic}; `priorAuthStatusId` stores the
 * pre-transition status for the modal. Auth-required rows reset both mode
 * and detail to blank. A non-empty previous value is kept when the new
 * status is not auth-required. No-auth-required rows without a previous
 * value default to `not_applicable`. `hasError` is true
 * only when the cell is required and the new status is not no-auth-required.
 *
 * @param rowData - Row with the new PA-required status already merged
 * @param oldAuthStatus - PA-required status before this edit
 * @param newAuthStatus - PA-required status after this edit
 * @returns Row with a refreshed `filingInfo` cell
 */
export const recomputeFilingInfo = (
  rowData: any,
  oldAuthStatus: string | undefined,
  newAuthStatus: string | undefined,
) => {
  const isNoAuthRequired =
    newAuthStatus === MedicalPaOrdersAuthStatus.NoAuthRequired;
  const isAuthRequired =
    newAuthStatus === MedicalPaOrdersAuthStatus.AuthRequired;
  const isRequired = getFilingInfoRequired(oldAuthStatus, newAuthStatus);
  const previousFilingInfo = rowData[CptCodeWithDetailsKeys.FilingInfo] ?? {};

  /**
   * Resolves mode or detail after a PA-required status change.
   *
   * Priority: auth-required clears the field, a non-empty previous value is
   * kept, and no-auth-required falls back to `not_applicable`.
   *
   * @param previous - Previously saved filing-info field
   * @returns Cleared, preserved, or defaulted filing-info value
   */
  const nextFilingField = (previous: string | undefined): string => {
    if (isAuthRequired) {
      return "";
    }
    if ((previous?.trim() ?? "").length > 0) {
      return previous ?? "";
    }
    if (isNoAuthRequired) {
      return FILING_MODE.NOT_APPLICABLE;
    }
    return "";
  };

  return {
    ...rowData,
    [CptCodeWithDetailsKeys.FilingInfo]: {
      ...previousFilingInfo,
      isRequired,
      isStatic: shouldTheFilingInfoBeStatic(oldAuthStatus, newAuthStatus),
      priorAuthStatusId: oldAuthStatus,
      value: nextFilingField(previousFilingInfo?.value),
      detail: nextFilingField(previousFilingInfo?.detail),
      hasError: isRequired && !isNoAuthRequired,
    },
  };
};

/**
 * Applies a Filing Info modal save to the row's Filing Info cell.
 *
 * `value` holds the filing mode and `detail` the portal name or fax number.
 * The modal gates Save on a valid pair, so the cell error clears here.
 *
 * @param rowDataToUpdate - Row whose Filing Info cell was edited
 * @param updatedData - Payload emitted by the Filing Info modal
 * @returns Row with the saved filing mode and detail
 */
export const updateRowDataForFilingInfo = (
  rowDataToUpdate: CptCodesTableRowModel,
  updatedData: FilingInfoSavePayload,
): CptCodesTableRowModel => ({
  ...rowDataToUpdate,
  [CptCodeWithDetailsKeys.FilingInfo]: {
    ...rowDataToUpdate[CptCodeWithDetailsKeys.FilingInfo],
    value: updatedData?.filingMode ?? "",
    detail: updatedData?.filingDetail ?? "",
    hasError: false,
  },
});
